/**
 * Maintenance repository — the data-access home for the `maintenance` slice.
 *
 * The ONLY place that touches `maintenance_requests` + `maintenance_assignments`
 * data. Strangler-Fig per-slice migration (manifest-6 / Track A6):
 *   Step 1 (done) — consolidated scattered `supabase.from("maintenance_requests")`
 *                   and `supabase.from("maintenance_assignments")` here.
 *   Step 2 (done) — guarded NestJS endpoints (`apps/api/src/maintenance/*`) + the
 *                   shared `maintenanceRequestSchema` / `maintenanceAssignmentSchema`.
 *   Step 3 (done) — STAFF request + assignment ops flipped to the Nest client.
 *   Step 4 (HERE) — the `maintenance_requests` fallbacks are REMOVED: that
 *                   table's staff/employee/owner SELECT policies were retired
 *                   (Track E), so those fallbacks could only return a silent
 *                   `[]` (or 42501 on a RETURNING write) for every non-super-admin
 *                   session — masking a Nest outage as an empty dashboard.
 *                   Nest is now the sole path and its failures surface loudly.
 *
 * ── Flip scope (owner-vs-staff authz split) ──────────────────────────────────
 *   NEST-ONLY, no fallback: `listRequests`, `getRequest`, `createRequest`,
 *     `updateRequest`, `deleteRequest`, `listStats`, `listForReports`,
 *     `listByProperty`, `listForUnifiedKanban`, `listByOwnerId`,
 *     `listByTenantIdForStaff` — every one read/wrote `maintenance_requests`,
 *     whose staff/employee/owner SELECT policies are asserted ABSENT by
 *     supabase/db-tests/tests/005_two_layer_access_model.sh.
 *   FLIPPED to Nest, Supabase fallback still ARMED (the fallback still works):
 *     - `listAssignments`, `createAssignment`, `updateAssignment` — they target
 *       `maintenance_assignments`, which KEEPS its live
 *       "Users can view company maintenance assignments" SELECT policy.
 *     - `listByTenantId` — the tenant portal read; `maintenance_requests` keeps
 *       a live "Tenants can view their maintenance requests" SELECT policy.
 *   KEPT on Supabase/RLS (documented):
 *     - the SERVICE-PROVIDER portal ops (`listProviderWorkOrders`,
 *        `getAssignmentForProvider`, `updateAssignment*`/`deleteAssignment` from
 *        the provider flows) — providers are a profile type with NO `maintenance.*`
 *        permission key, so a guarded `maintenance` endpoint would 403 them (the
 *        live-observed regression the slice-migration skill warns about). They get
 *        their own owner/provider-appropriate endpoints in a later slice.
 *
 *   Employee property-scoping on the staff list is resolved server-side by the
 *   Nest oracle; `assignedPropertyIds` is no longer read here (it only ever fed
 *   the removed Supabase filter).
 *
 * ── Convention audit (delete semantics) ──────────────────────────────────────
 *   Neither `maintenance_requests` nor `maintenance_assignments` has a
 *   `deleted_at` / `is_active` column (verified against the generated types), and
 *   no read filters one — so a hard `.delete()` is the CORRECT semantics here (no
 *   soft-delete fix; unlike contracts/owners). The staff request delete and the
 *   provider reject (assignment delete) remain hard deletes, byte-identical to the
 *   pre-migration hooks.
 *
 * Deferred (NOT migrated here): the cross-domain `supabase.from("maintenance_*")`
 * reads in hooks/useTenants, hooks/useTenantData, hooks/useOwnerData,
 * hooks/useUnifiedTasks, hooks/useReportsData, hooks/useDashboardStats,
 * hooks/usePropertyAggregates, hooks/useEmployeeData, and
 * components/dashboard/ActivityFeed — see the deferred backlog
 * (domain-debugging-matrix "read-by" column).
 */
import { isDefinitiveNotFound, reportNestFallback, reportNestFallbackOrRethrowUnknownCommit } from "@/lib/nestFallback";
import { z } from "zod";
import { maintenanceRequestSchema, maintenanceAssignmentSchema, type MaintenanceRequestStatus, MAINTENANCE_COMPLETED_STATUSES, MAINTENANCE_PENDING_STATUSES, MAINTENANCE_CANCELLED_STATUSES } from "@dallali/shared";
import { supabase } from "@/integrations/supabase/client";
import { serializeError } from "@/lib/sentry";
import type { Database } from "@/integrations/supabase/types";
import { API_ROUTES } from "@/api/routes";
import { API_URL } from "@/services/api";
import { makeNestRequest } from "@/services/nestRequest";


/** Both maintenance tables live in the public Supabase schema. Centralized here. */
const REQUESTS_TABLE = "maintenance_requests";
const ASSIGNMENTS_TABLE = "maintenance_assignments";

// Authenticated Nest request bound to the "maintenance" slice — shared transport
// (timeout + retry + typed NestRequestError). See src/services/nestRequest.ts.
const nestRequest = makeNestRequest("maintenance");

type MaintenanceRequestInsert = Database["public"]["Tables"]["maintenance_requests"]["Insert"];
type MaintenanceRequestUpdate = Database["public"]["Tables"]["maintenance_requests"]["Update"];
type MaintenanceAssignmentInsert =
  Database["public"]["Tables"]["maintenance_assignments"]["Insert"];
type MaintenanceAssignmentUpdate =
  Database["public"]["Tables"]["maintenance_assignments"]["Update"];

// ─── Public data shapes ──────────────────────────────────────────────────────

/**
 * A provider work order from GET /maintenance/provider — an assignment row with
 * its request (+ property/unit/tenant) nested under `maintenance_requests`,
 * byte-identical to the previous PostgREST select shape so the provider hooks
 * read it unchanged.
 */
export interface ProviderWorkOrder {
  id: string;
  company_id: string;
  request_id: string;
  provider_id: string | null;
  assigned_at: string;
  scheduled_date: string | null;
  estimated_cost: number | null;
  actual_cost: number | null;
  completion_date: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  maintenance_requests: {
    id: string;
    title: string;
    description: string | null;
    category: string;
    priority: string;
    status: string;
    images: unknown;
    notes: string | null;
    created_at: string;
    property: {
      id: string;
      name: string;
      address: string | null;
      city: string | null;
      district: string | null;
    } | null;
    unit: { id: string; unit_number: string } | null;
    tenant: {
      id: string;
      first_name: string;
      last_name: string;
      phone: string | null;
    } | null;
  };
}

export interface MaintenanceRequestListParams {
  companyId: string;
  /** Non-null = employee scoped to these property ids; null = no assignment filter (admin). */
  assignedPropertyIds: string[] | null;
}

/** Create payload — company_id is injected by the repository, never trusted from a body. */
export type CreateMaintenanceRequestData = Omit<
  MaintenanceRequestInsert,
  "id" | "company_id" | "created_at" | "updated_at"
>;

/** Create payload for an assignment — company_id injected by the repository. */
export type CreateMaintenanceAssignmentData = Omit<
  MaintenanceAssignmentInsert,
  "id" | "company_id" | "created_at" | "updated_at"
>;

// ─── Select strings (mirror the Nest repo .select() byte-for-byte) ───────────

const ASSIGNMENT_LIST_SELECT = `
          *,
          provider:service_providers(*)
        `;

// ── Supabase implementations (fallback for the still-armed assignment methods,
//    and the live path for the provider-portal + tenant/owner-portal ops) ─────

type MaintenanceRequestRow = Database["public"]["Tables"]["maintenance_requests"]["Row"];

/** Row shapes the staff request reads resolve to — the request row plus the
 *  relations the Nest endpoint embeds. Mirrors the PostgREST select shape the
 *  retired fallback used to produce, so consumers are unaffected. */
type RequestListResult = (MaintenanceRequestRow & {
  property: { name: string; name_en: string | null } | null;
  unit: { unit_number: string } | null;
  tenant: {
    first_name: string;
    last_name: string;
    first_name_en: string | null;
    last_name_en: string | null;
  } | null;
})[];

type RequestDetailResult =
  | (MaintenanceRequestRow & {
      property: { name: string; name_en: string | null } | null;
      unit: { unit_number: string } | null;
      tenant: {
        first_name: string;
        last_name: string;
        first_name_en: string | null;
        last_name_en: string | null;
        phone: string | null;
        email: string | null;
      } | null;
    })
  | null;

async function listAssignmentsViaSupabase(requestId: string) {
  const { data, error } = await supabase
    .from(ASSIGNMENTS_TABLE)
    .select(ASSIGNMENT_LIST_SELECT)
    .eq("request_id", requestId)
    .order("assigned_at", { ascending: false });

  if (error) {
    console.error("Error fetching maintenance assignments:", serializeError(error));
    throw error;
  }
  return data ?? [];
}

type AssignmentListResult = Awaited<ReturnType<typeof listAssignmentsViaSupabase>>;

export const maintenanceRepository = {
  // ─── STAFF requests (flipped to Nest, Supabase fallback) ────────────────────

  /**
   * Canonical staff maintenance-request list with property/unit/tenant relations.
   * Always company-scoped. When `assignedPropertyIds` is a non-empty array the
   * Supabase fallback restricts to those properties (employee scope); the Nest
   * path resolves employee scoping server-side via the oracle.
   *
   * Staff op — flipped to Nest (oracle-scoped staffList), Supabase fallback.
   */
  async listRequests(_params: MaintenanceRequestListParams) {
    const rows = await nestRequest<unknown[]>(API_ROUTES.maintenance.staffList());
    return z.array(maintenanceRequestSchema).parse(rows) as unknown as RequestListResult;
  },

  /**
   * Single maintenance request by id with detailed relations. Company-scoped.
   * Staff op — flipped to Nest, Supabase fallback.
   */
  async getRequest(id: string, _companyId: string) {
    try {
      const row = await nestRequest<unknown>(API_ROUTES.maintenance.getById(id));
      return (row ? maintenanceRequestSchema.parse(row) : null) as unknown as RequestDetailResult;
    } catch (err) {
      // A definitive 404 is Nest's real answer (absent / out-of-scope row).
      if (isDefinitiveNotFound(err)) return null;
      throw err;
    }
  },

  /**
   * Insert a maintenance request. company_id is injected from the trusted
   * call-site value (never read from a free-form body field).
   *
   * Staff op — flipped to Nest (POST), Supabase fallback.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the Supabase
   * fallback only runs when Nest verifiably did not commit (safe 4xx /
   * unconfigured). An ambiguous outcome (network, timeout, 5xx, parse failure
   * after a 2xx) RETHROWS instead of re-issuing the insert — a fallback after a
   * committed-but-lost Nest write would double-create the request.
   */
  async createRequest(companyId: string, data: CreateMaintenanceRequestData) {
    return await nestRequest<unknown>(API_ROUTES.maintenance.create(), {
      method: "POST",
      body: JSON.stringify({ ...data, company_id: companyId }),
    });
  },

  /**
   * Update mutable fields of a maintenance request (status change, notes, etc.).
   * Company-scoped. Returns the updated row; throws if not found / access denied.
   *
   * Staff op — flipped to Nest (PUT), Supabase fallback.
   */
  async updateRequest(id: string, _companyId: string, data: MaintenanceRequestUpdate) {
    const row = await nestRequest<unknown>(API_ROUTES.maintenance.update(id), {
      method: "PUT",
      body: JSON.stringify(data),
    });
    return maintenanceRequestSchema.parse(row);
  },

  /**
   * Hard-delete a maintenance request (correct semantics — no `deleted_at`
   * column on this table). Company-scoped.
   *
   * Staff op — flipped to Nest (DELETE → 204), Supabase fallback.
   */
  async deleteRequest(id: string, _companyId: string): Promise<void> {
    await nestRequest<void>(API_ROUTES.maintenance.remove(id), { method: "DELETE" });
  },

  // ─── STAFF assignments (flipped to Nest, Supabase fallback) ─────────────────

  /**
   * List the assignments for a request (with the provider relation), newest
   * first. The assignments are reached only from the staff request-detail page,
   * so this filters by `request_id` (the request itself is already company-scoped
   * on the staff path). Staff op — flipped to Nest, Supabase fallback.
   */
  async listAssignments(requestId: string) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(API_ROUTES.maintenance.assignments(requestId));
        return z.array(maintenanceAssignmentSchema).parse(rows) as unknown as AssignmentListResult;
      } catch (err) {
        reportNestFallback("maintenance.listAssignments", err);
      }
    }
    return listAssignmentsViaSupabase(requestId);
  },

  /**
   * Create a maintenance assignment (assign a provider to a request).
   * company_id is injected from the trusted call-site value.
   *
   * Staff op — flipped to Nest (POST), Supabase fallback.
   *
   * NON-IDEMPOTENT write → duplicate-write guard (#3316 / #3399): the Supabase
   * fallback only runs when Nest verifiably did not commit (safe 4xx /
   * unconfigured). An ambiguous outcome (network, timeout, 5xx, parse failure
   * after a 2xx) RETHROWS instead of re-issuing the insert — a fallback after a
   * committed-but-lost Nest write would double-create the assignment.
   */
  async createAssignment(companyId: string, data: CreateMaintenanceAssignmentData) {
    if (API_URL) {
      try {
        return await nestRequest<unknown>(API_ROUTES.maintenance.createAssignment(), {
          method: "POST",
          body: JSON.stringify({ ...data, company_id: companyId }),
        });
      } catch (err) {
        reportNestFallbackOrRethrowUnknownCommit("maintenance.createAssignment", err);
      }
    }
    const { data: result, error } = await supabase
      .from(ASSIGNMENTS_TABLE)
      .insert({ ...data, company_id: companyId })
      .select()
      .single();

    if (error) {
      console.error("Error creating maintenance assignment:", serializeError(error));
      throw error;
    }
    return result;
  },

  /**
   * Update a maintenance assignment (schedule, cost, completion, notes).
   * Company-scoped. Returns the updated row; throws if not found / access denied.
   *
   * Staff op — flipped to Nest (PUT), Supabase fallback.
   */
  async updateAssignment(id: string, companyId: string, data: MaintenanceAssignmentUpdate) {
    if (API_URL) {
      try {
        const row = await nestRequest<unknown>(API_ROUTES.maintenance.updateAssignment(id), {
          method: "PUT",
          body: JSON.stringify(data),
        });
        return maintenanceAssignmentSchema.parse(row);
      } catch (err) {
        reportNestFallback("maintenance.updateAssignment", err);
      }
    }
    const { data: result, error } = await supabase
      .from(ASSIGNMENTS_TABLE)
      .update(data)
      .eq("id", id)
      .eq("company_id", companyId)
      .select()
      .maybeSingle();

    if (error) {
      console.error("Error updating maintenance assignment:", serializeError(error));
      throw error;
    }
    if (!result) throw new Error("Maintenance assignment not found or access denied");
    return result;
  },

  // ─── SERVICE-PROVIDER portal ops (FLIPPED to Nest — server resolves identity) ─
  //
  // Providers hold no `maintenance.*` permission key, so these use the dedicated
  // /maintenance/provider/* endpoints (SupabaseAuthGuard only). The provider id is
  // resolved SERVER-SIDE from the JWT via the user_id FK, so the FE passes neither
  // a providerId nor a companyId — and there is NO Supabase fallback (the by-phone
  // provider lookup these relied on has been removed). The endpoints are
  // fail-closed: a non-linked caller gets [] (list) or 404 (actions). The server
  // also enforces per-assignment ownership (the RLS row-scope, now in code).

  /** The caller's work orders (assignments + request/property/unit/tenant). */
  async listProviderWorkOrders(): Promise<ProviderWorkOrder[]> {
    return nestRequest<ProviderWorkOrder[]>(
      API_ROUTES.maintenance.providerWorkOrders(),
    );
  },

  /** Accept a work order → request status `assigned`. */
  async acceptWorkOrder(assignmentId: string): Promise<void> {
    await nestRequest<unknown>(API_ROUTES.maintenance.providerAccept(assignmentId), {
      method: "POST",
    });
  },

  /** Reject a work order → delete the assignment + request status `pending`. */
  async rejectWorkOrder(assignmentId: string): Promise<void> {
    await nestRequest<unknown>(API_ROUTES.maintenance.providerReject(assignmentId), {
      method: "POST",
    });
  },

  /** Start work → request status `in_progress`. */
  async startWork(assignmentId: string): Promise<void> {
    await nestRequest<unknown>(API_ROUTES.maintenance.providerStart(assignmentId), {
      method: "POST",
    });
  },

  /** Complete work → completion date + actual cost, request status `completed`. */
  async completeWork(assignmentId: string, actualCost?: number): Promise<void> {
    await nestRequest<unknown>(API_ROUTES.maintenance.providerComplete(assignmentId), {
      method: "POST",
      body: JSON.stringify({ actual_cost: actualCost ?? null }),
    });
  },

  /** Append a note to the work order (server merges with existing notes). */
  async addProviderNote(assignmentId: string, note: string): Promise<void> {
    await nestRequest<unknown>(API_ROUTES.maintenance.providerNote(assignmentId), {
      method: "POST",
      body: JSON.stringify({ note }),
    });
  },

  /** Submit a price quote (estimate) for the work order. */
  async submitProviderQuote(
    assignmentId: string,
    data: { estimatedCost?: number; notes?: string | null },
  ): Promise<void> {
    await nestRequest<unknown>(API_ROUTES.maintenance.providerQuote(assignmentId), {
      method: "POST",
      body: JSON.stringify({
        estimated_cost: data.estimatedCost ?? null,
        notes: data.notes ?? null,
      }),
    });
  },

  // ─── Staff aggregate reads (Nest-first, Supabase fallback) ──────────────

  /**
   * Status counts for the dashboard stats widget. Oracle-scoped via
   * GET /maintenance/stats — admin sees all, employee sees assigned properties.
   * Staff op — flipped to Nest, Supabase fallback.
   */
  async listStats(_companyId: string) {
    return await nestRequest<{ total: number; completed: number; pending: number; cancelled: number }>(
      API_ROUTES.maintenance.stats(),
    );
  },

  /**
   * Enriched request + assignment report data for the reports page.
   * Oracle-scoped via GET /maintenance/reports — admin sees all, employee
   * sees assigned properties.
   * Staff op — flipped to Nest, Supabase fallback.
   */
  async listForReports(
    _companyId: string,
    opts: { startDate?: string; endDate?: string },
  ) {
    return await nestRequest<{ requests: unknown[]; assignments: unknown[] }>(
      API_ROUTES.maintenance.reports(opts.startDate, opts.endDate),
    );
  },

  /**
   * Maintenance requests filtered by a single property. Oracle-scoped via
   * GET /maintenance/staff?property_id= — admin sees the property's requests,
   * employee sees only if assigned to that property.
   * Staff op — flipped to Nest, Supabase fallback.
   */
  async listByProperty(_companyId: string, propertyId: string) {
    const rows = await nestRequest<unknown[]>(
      API_ROUTES.maintenance.staffList(propertyId),
    );
    return z.array(maintenanceRequestSchema).parse(rows);
  },

  /**
   * The newest maintenance requests (with unit/property names) for the
   * dashboard activity feed. Company-scoped; throws on error. KEPT on
   * Supabase — dashboard aggregate read.
   */
  async listRecent(companyId: string, limit: number) {
    const { data, error } = await supabase
      .from(REQUESTS_TABLE)
      .select('id, title, description, status, priority, category, created_at, unit:units(unit_number), property:properties(name)')
      .eq('company_id', companyId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return data || [];
  },

  /**
   * Maintenance requests for the unified kanban board. Oracle-scoped via
   * GET /maintenance/staff — admin sees all, employee sees assigned properties.
   * Staff op — flipped to Nest, Supabase fallback.
   */
  async listForUnifiedKanban(_companyId: string) {
    const rows = await nestRequest<unknown[]>(API_ROUTES.maintenance.staffList());
    return z.array(maintenanceRequestSchema).parse(rows);
  },

  // ─── Owner + tenant portal reads (Nest-first, Supabase fallback) ──────

  /**
   * Owner-context read — returns only the identified owner's maintenance
   * requests, scoped by owner_id within their company. Portal endpoint:
   * GET /maintenance/owner (no PermissionGuard — self-service).
   */
  async listByOwnerId(_ownerId: string, _companyId: string) {
    const rows = await nestRequest<unknown[]>(API_ROUTES.maintenance.ownerList());
    return z.array(maintenanceRequestSchema).parse(rows);
  },

  /**
   * Tenant-context read — returns only the identified tenant's maintenance
   * requests, scoped by tenant_id within their company. Portal endpoint:
   * GET /maintenance/tenant (no PermissionGuard — self-service).
   *
   * Optional `range` bounds `created_at` (month/year time filter, CEO ask —
   * the TenantDashboard "Total Services" stat card); applied on BOTH the Nest
   * path (query params) and the Supabase fallback (.gte()/.lte()) so a Nest
   * outage doesn't silently drop the filter.
   */
  async listByTenantId(
    tenantId: string,
    companyId: string,
    range?: { startDate?: string; endDate?: string },
  ) {
    if (API_URL) {
      try {
        const rows = await nestRequest<unknown[]>(
          API_ROUTES.maintenance.tenantList(range?.startDate, range?.endDate),
        );
        return z.array(maintenanceRequestSchema).parse(rows);
      } catch (err) {
        reportNestFallback("maintenance.listByTenantId", err);
      }
    }
    // Supabase fallback — mirrors the pre-migration useTenantMaintenance query
    let query = supabase
      .from(REQUESTS_TABLE)
      .select(`*, unit:units(id, unit_number), property:properties(id, name)`)
      .eq("tenant_id", tenantId)
      .eq("company_id", companyId);

    if (range?.startDate) query = query.gte("created_at", range.startDate);
    if (range?.endDate) query = query.lte("created_at", range.endDate);

    const { data, error } = await query.order("created_at", { ascending: false });
    if (error) throw error;
    return data ?? [];
  },

  // ─── Staff-side tenant-maintenance SELECT (Nest-first, Supabase fallback) ──

  /**
   * Staff-side read — returns maintenance requests for a specific tenant within
   * the company. Used by the tenant detail page (dashboard, staff-facing).
   * Nest path: GET /maintenance/staff?tenant_id= → server-scoped by tenant.
   */
  async listByTenantIdForStaff(tenantId: string, _companyId: string) {
    const rows = await nestRequest<unknown[]>(
      API_ROUTES.maintenance.staffList(undefined, tenantId),
    );
    return z.array(maintenanceRequestSchema).parse(rows);
  },

  // ─── Owner + tenant portal writes (NEST-ONLY — server resolves the caller) ──
  //
  // Self-service endpoints (SupabaseAuthGuard only): the server resolves the
  // owner/tenant from the JWT and reads company/property/unit/owner from the
  // tenant's active contract, so the FE sends none of them. No Supabase
  // fallback — like the staff writes, a Nest failure surfaces loudly.

  /**
   * Owner portal: approve/reject a maintenance request on their property.
   * POST /maintenance/owner/:id/decision — 409 once decided or when the request
   * does not need the owner's approval.
   */
  async approveByOwner(requestId: string, approved: boolean): Promise<void> {
    await nestRequest<unknown>(API_ROUTES.maintenance.ownerDecision(requestId), {
      method: "POST",
      body: JSON.stringify({ approved }),
    });
  },

  /**
   * Tenant portal: submit a new maintenance request. POST /maintenance/tenant —
   * the request lands on the tenant's active contract with status `new`
   * (422 when there is no active contract). `images` are bare
   * `documents`-bucket paths under `<companyId>/maintenance/`.
   */
  async createRequestForTenant(data: {
    title: string;
    category: string;
    description: string;
    priority: string;
    images?: string[];
  }) {
    const row = await nestRequest<unknown>(API_ROUTES.maintenance.tenantCreate(), {
      method: "POST",
      body: JSON.stringify(data),
    });
    return maintenanceRequestSchema.parse(row);
  },
};
